import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useOpenOnNew from '../hooks/useOpenOnNew';
import { useAuth } from '../context/AuthContext';
import { getContacts, getAccounts, getLeadSources, createContact, bulkUpdateContacts, importContacts, getUsers, getRepFirms, exportContactsExcel, exportContactsPdf, optional } from '../api';
import Modal from '../components/Modal';
import SortableHeader from '../components/SortableHeader';
import ExportMenu from '../components/ExportMenu';
import CsvImportModal from '../components/CsvImportModal';
import Pagination from '../components/Pagination';
import { ColumnsButton } from '../components/ColumnConfigurator';
import SavedViews from '../components/SavedViews';
import MultiSelectFilter from '../components/MultiSelectFilter';
import { TableSkeleton, CardSkeleton } from '../components/TableSkeleton';
import RowActions from '../components/RowActions';
import KeyStar from '../components/KeyStar';
import EmptyValue from '../components/EmptyValue';
import MobileCard from '../components/MobileCard';
import useColumns from '../hooks/useColumns';
import usePagination from '../hooks/usePagination';
import useMediaQuery from '../hooks/useMediaQuery';
import { Plus, Search, Users, Edit, ChevronDown, Upload, X, Eye } from 'lucide-react';
import EmptyState, { EmptyStateFiltered } from '../components/EmptyState';
import './Contacts.css';

function Contacts() {
  const { canDo, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { visibleColumns } = useColumns('contacts');
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Lookup data (not paginated)
  const [accounts, setAccounts] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [users, setUsers] = useState([]);
  const [repFirms, setRepFirms] = useState([]);
  const [lookupError, setLookupError] = useState('');
  const [modalError, setModalError] = useState('');

  // Filters - initialize from URL params
  const [accountFilter, setAccountFilter] = useState(() => {
    const param = searchParams.get('account_id');
    return param ? param.split(',') : [];
  });
  const [activeFilter, setActiveFilter] = useState(searchParams.get('active') || '');

  // Build extra params for the pagination hook
  const extraParams = useMemo(() => {
    const p = {};
    if (accountFilter.length) p.account_id = accountFilter.join(',');
    if (activeFilter) p.active = activeFilter;
    return p;
  }, [accountFilter, activeFilter]);

  // Server-side pagination via hook
  const {
    data: contacts,
    loading,
    error: paginationError,
    page,
    setPage,
    limit,
    setLimit,
    search,
    setSearch,
    sortBy: sortField,
    sortOrder: sortDirection,
    handleSort,
    total,
    totalPages,
    refetch,
  } = usePagination(getContacts, {
    queryKey: 'contacts',
    initialLimit: 25,
    initialSortBy: 'first_name',
    initialSortOrder: 'asc',
    debounceMs: 300,
    extraParams,
  });

  const error = lookupError || paginationError;

  // A first click on Key puts the starred contacts at the top; every other
  // column starts ascending as usual.
  const sortContacts = (field, direction) => handleSort(field, field === 'is_key_contact' && sortField !== 'is_key_contact' ? 'desc' : direction);

  // Selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false); // false | 'owner'

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMore, setShowMore] = useState(false);
  useOpenOnNew(() => setShowCreateModal(true));
  const [showImportModal, setShowImportModal] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    job_title: '',
    account_id: searchParams.get('account_id') || '',
    active: true,
    market_to: true,
    lead_source_id: '',
    owner_id: user?.id || '', // whoever creates the contact owns it unless they say otherwise
    rep_firm_id: '',
    notes: '',
  });

  // Load lookup data once
  useEffect(() => {
    const loadLookups = async () => {
      try {
        // Accounts and rep firms may be module-restricted for this user; a
        // hidden module degrades to an empty dropdown, not an error banner.
        const [accountsRes, sourcesRes, usersRes, repFirmsRes] = await Promise.all([
          optional(getAccounts()),
          getLeadSources(),
          getUsers(),
          optional(getRepFirms()),
        ]);
        setAccounts(Array.isArray(accountsRes.data) ? accountsRes.data : []);
        setLeadSources(sourcesRes.data);
        setUsers(usersRes.data);
        setRepFirms(repFirmsRes.data);
      } catch (err) {
        setLookupError('Failed to load filter options');
      }
    };
    loadLookups();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const response = await createContact(formData);
      setShowCreateModal(false);
      resetForm();
      navigate(`/contacts/${response.data.id}`);
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to create contact');
    }
  };

  const handleBulkUpdate = async (updates) => {
    try {
      await bulkUpdateContacts(selectedIds, updates);
      setSelectedIds([]);
      setShowBulkActions(false);
      refetch();
    } catch (err) {
      setLookupError(err.response?.data?.error || 'Failed to update contacts');
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === contacts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(contacts.map(c => c.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const resetForm = () => {
    setModalError('');
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      job_title: '',
      account_id: '',
      active: true,
      market_to: true,
      lead_source_id: '',
      owner_id: user?.id || '',
      rep_firm_id: '',
      notes: '',
    });
  };

  // Extracted renderCell to accept (col, contact) for MobileCard compatibility
  // The star saves straight away and the page re-reads, so sorting by Key
  // reflects the change without leaving the list.
  const toggleKeyContact = async (contact, on) => {
    try {
      await bulkUpdateContacts([contact.id], { is_key_contact: on });
      refetch();
    } catch (err) { setLookupError(err.response?.data?.error || 'Could not update the key-contact flag'); }
  };

  const renderCell = (col, contact) => {
    switch (col.key) {
      case 'is_key_contact':
        return <KeyStar on={!!contact.is_key_contact} name={contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim()} onToggle={canDo('contacts', 'edit') ? (on) => toggleKeyContact(contact, on) : undefined} />;
      case 'first_name':
        return <Link to={`/contacts/${contact.id}`} className="contact-name-link">{contact.first_name || contact.name?.split(' ')[0] || <EmptyValue />}</Link>;
      case 'last_name':
        return contact.last_name || contact.name?.split(' ').slice(1).join(' ') || <EmptyValue />;
      case 'account_name':
        return contact.account_name ? <Link to={`/accounts/${contact.account_id}`}>{contact.account_name}</Link> : <EmptyValue />;
      case 'email':
        return contact.email || <EmptyValue />;
      case 'phone':
        return contact.phone || <EmptyValue />;
      case 'job_title':
        return contact.job_title || <EmptyValue />;
      case 'city':
        return contact.city || <EmptyValue />;
      case 'state':
        return contact.state || <EmptyValue />;
      case 'country':
        return contact.country || <EmptyValue />;
      case 'active':
        return (
          <div className="status-badges">
            <span className={`badge ${contact.active ? 'badge-success' : 'badge-gray'}`}>{contact.active ? 'Active' : 'Inactive'}</span>
            {!!contact.market_to && <span className="badge badge-primary">Marketable</span>}
          </div>
        );
      case 'owner_name':
        return contact.owner_name || <EmptyValue />;
      case 'rep_firm_name':
        return contact.rep_firm_name || <EmptyValue />;
      case 'lead_source_name':
        return contact.lead_source_name || <EmptyValue />;
      case 'created_at':
        return contact.created_at ? new Date(contact.created_at).toLocaleDateString() : <EmptyValue />;
      default:
        return contact[col.key] || <EmptyValue />;
    }
  };

  // Build row actions for a contact
  const getRowActions = (contact) => {
    const actions = [
      {
        key: 'view',
        label: 'View',
        icon: Eye,
        onClick: () => navigate(`/contacts/${contact.id}`),
      },
    ];
    if (canDo('contacts', 'edit')) {
      actions.push({
        key: 'edit',
        label: 'Edit',
        icon: Edit,
        onClick: () => navigate(`/contacts/${contact.id}`),
      });
    }
    return actions;
  };

  return (
    <div className="contacts-page">
      <div className="page-header">
        <h1 className="page-title">Contacts</h1>
        <div className="page-actions">
          <SavedViews entityType="contacts" current={{ search, accountFilter, activeFilter, sortBy: sortField, sortOrder: sortDirection }} onApply={(v) => { setSearch(v.search || ''); setAccountFilter(v.accountFilter || []); setActiveFilter(v.activeFilter || ''); if (v.sortBy) handleSort(v.sortBy, v.sortOrder || 'asc'); }} />
          <ColumnsButton entityType="contacts" />
          <ExportMenu
            onExportExcel={exportContactsExcel}
            onExportPdf={exportContactsPdf}
            entityName="Contacts"
          />
          {canDo('contacts', 'create') && (
            <button className="btn btn-secondary" onClick={() => setShowImportModal(true)}>
              <Upload size={18} />
              Import CSV
            </button>
          )}
          {canDo('contacts', 'create') && (
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
              <Plus size={18} />
              New Contact
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error" role="alert" aria-live="assertive">{error}</div>}

      {/* Search and Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="filter-bar">
            <div className="search-input">
              <Search size={18} className="search-input-icon" aria-hidden="true" />
              <input
                type="text"
                className="form-input"
                placeholder="Search contacts..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search contacts"
              />
            </div>
            <MultiSelectFilter
              label="Accounts"
              options={accounts}
              selected={accountFilter}
              onChange={setAccountFilter}
              className="filter-dropdown-md"
              ariaLabel="Filter by account"
            />
            <select
              className="form-select filter-dropdown-sm"
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
            {(search || accountFilter.length > 0 || activeFilter) && (
              <button
                className="btn-clear-filters"
                onClick={() => { setSearch(''); setAccountFilter([]); setActiveFilter(''); }}
              >
                <X size={14} />
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <div className="bulk-actions-bar">
          <span>{selectedIds.length} selected</span>
          <div className="bulk-actions">
            {canDo('contacts', 'edit') && (
              <>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleBulkUpdate({ active: true })}
                >
                  Set Active
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleBulkUpdate({ active: false })}
                >
                  Set Inactive
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleBulkUpdate({ market_to: true })}
                >
                  Allow Marketing
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleBulkUpdate({ market_to: false })}
                >
                  Block Marketing
                </button>
                <div className="dropdown">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowBulkActions(showBulkActions === 'owner' ? false : 'owner')}
                  >
                    Change Owner
                    <ChevronDown size={16} />
                  </button>
                  {showBulkActions === 'owner' && (
                    <div className="dropdown-menu">
                      {users.map(u => (
                        <button
                          key={u.id}
                          className="dropdown-item"
                          onClick={() => handleBulkUpdate({ owner_id: u.id })}
                        >
                          {u.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Contacts Table / Mobile Cards */}
      <div className="card">
        {isMobile ? (
          /* Mobile Card Layout */
          <div className="mobile-card-list">
            {loading && contacts.length === 0 ? (
              <CardSkeleton count={4} />
            ) : contacts.length === 0 ? (
              search || accountFilter.length > 0 || activeFilter ? (
                <EmptyStateFiltered
                  onClearFilters={() => { setSearch(''); setAccountFilter([]); setActiveFilter(''); }}
                />
              ) : (
                <EmptyState
                  icon={Users}
                  title="No contacts yet"
                  description="Add your first contact to start building your network."
                  actionLabel={canDo('contacts', 'create') ? 'New Contact' : undefined}
                  onAction={canDo('contacts', 'create') ? () => setShowCreateModal(true) : undefined}
                  actionIcon={Plus}
                />
              )
            ) : (
              contacts.map(contact => (
                <MobileCard
                  key={contact.id}
                  columns={visibleColumns}
                  data={contact}
                  renderCell={renderCell}
                  primaryField="first_name"
                  selected={selectedIds.includes(contact.id)}
                  onSelect={() => toggleSelect(contact.id)}
                  actions={<RowActions id={contact.id} actions={getRowActions(contact)} />}
                />
              ))
            )}
          </div>
        ) : (
          /* Desktop Table Layout */
          <div className="table-container">
            <table className="table" aria-label="Contacts list">
              <thead>
                <tr>
                  <th scope="col" style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={selectedIds.length === contacts.length && contacts.length > 0}
                      onChange={toggleSelectAll}
                      aria-label="Select all contacts"
                    />
                  </th>
                  {visibleColumns.map(col => (
                    <SortableHeader key={col.key} label={col.label} field={col.key} sortField={sortField} sortDirection={sortDirection} onSort={sortContacts} />
                  ))}
                  <th scope="col" className="col-actions"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {loading && contacts.length === 0 ? (
                  <TableSkeleton columns={visibleColumns.length} rows={8} hasCheckbox={true} hasActions={true} />
                ) : contacts.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length + 2}>
                      {search || accountFilter.length > 0 || activeFilter ? (
                        <EmptyStateFiltered
                          onClearFilters={() => { setSearch(''); setAccountFilter([]); setActiveFilter(''); }}
                        />
                      ) : (
                        <EmptyState
                          icon={Users}
                          title="No contacts yet"
                          description="Add your first contact to start building your network."
                          actionLabel={canDo('contacts', 'create') ? 'New Contact' : undefined}
                          onAction={canDo('contacts', 'create') ? () => setShowCreateModal(true) : undefined}
                          actionIcon={Plus}
                        />
                      )}
                    </td>
                  </tr>
                ) : (
                  contacts.map(contact => (
                    <tr key={contact.id}>
                      <td>
                        <input type="checkbox" className="checkbox" checked={selectedIds.includes(contact.id)} onChange={() => toggleSelect(contact.id)} />
                      </td>
                      {visibleColumns.map(col => (
                        <td key={col.key}>{renderCell(col, contact)}</td>
                      ))}
                      <td>
                        <RowActions id={contact.id} actions={getRowActions(contact)} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        {/* Server-side Pagination */}
        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      </div>

      {/* Create Contact Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        title="New Contact"
        size="lg"
      >
        <form onSubmit={handleCreate}>
          <div className="modal-body">
            {modalError && <div className="alert alert-error" role="alert">{modalError}</div>}
            <div className="grid grid-cols-2">
              <div className="form-group">
                <label className="form-label">First Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Account *</label>
              <select
                className="form-select"
                value={formData.account_id}
                onChange={(e) => setFormData({ ...formData, account_id: e.target.value })}
                required
              >
                <option value="">Select Account</option>
                {accounts.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>

            {showMore ? (
              <>
            <div className="grid grid-cols-2">
            <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  className="form-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2">
              <div className="form-group">
                <label className="form-label">Job Title</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.job_title}
                  onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Lead Source</label>
                <select
                  className="form-select"
                  value={formData.lead_source_id}
                  onChange={(e) => setFormData({ ...formData, lead_source_id: e.target.value })}
                >
                  <option value="">Select Lead Source</option>
                  {leadSources.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2">
              <div className="form-group">
                <label className="form-label">Owner</label>
                <select
                  className="form-select"
                  value={formData.owner_id}
                  onChange={(e) => setFormData({ ...formData, owner_id: e.target.value })}
                >
                  <option value="">Select Owner</option>
                  {users.filter(u => u.active).map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Rep Firm</label>
                <select
                  className="form-select"
                  value={formData.rep_firm_id}
                  onChange={(e) => setFormData({ ...formData, rep_firm_id: e.target.value })}
                >
                  <option value="">Select Rep Firm</option>
                  {repFirms.map(rf => (
                    <option key={rf.id} value={rf.id}>{rf.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2">
              <div className="form-group">
                <label className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
              <div className="form-group">
                <label className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={formData.market_to}
                    onChange={(e) => setFormData({ ...formData, market_to: e.target.checked })}
                  />
                  Allow Marketing
                </label>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                className="form-textarea"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>
              </>
            ) : (
              <p className="form-more">
                <button type="button" className="btn-link" onClick={() => setShowMore(true)}>More details…</button>
                <span className="text-muted text-sm"> — everything else can be filled in on the record.</span>
              </p>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => { setShowCreateModal(false); resetForm(); }}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Create Contact
            </button>
          </div>
        </form>
      </Modal>

      {/* Import CSV Modal */}
      <CsvImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={importContacts}
        entityName="Contacts"
        sampleColumns={['first_name', 'last_name', 'account', 'email', 'phone', 'job_title', 'city', 'state', 'country', 'lead_source', 'rep_firm', 'owner', 'notes', 'linkedin_url', 'active', 'market_to']}
        sampleData={[
          { first_name: 'John', last_name: 'Doe', account: 'Acme Corporation', email: 'john@acme.com', phone: '555-1234', job_title: 'VP Sales', city: 'San Jose', state: 'CA', country: 'USA', lead_source: 'Website', rep_firm: '', owner: '', notes: '', linkedin_url: '', active: 'true', market_to: 'true' }
        ]}
        requiredColumns={['first_name or name', 'account']}
        matchDescription="email address"
        onLoadData={refetch}
      />
    </div>
  );
}

export default Contacts;
